import React, { useState, useEffect, useCallback } from "react";
import { X, ImagePlus } from "lucide-react";
import Reveal from "./Reveal";
import MarbleImage from "./MarbleImage";

/**
 * Slab Gallery (single-slab preview)
 * Renders only the FIRST slab as a real preview tile + a blurred placeholder
 * tile inviting the user to "Request More Images". The blurred placeholder
 * uses the same first slab image so the layout looks consistent.
 *
 * Lightbox is restricted to the single visible slab.
 */
const SlabGallery = ({ slabs, lotId, onRequestMoreImages }) => {
  const [lightboxOpen, setLightboxOpen] = useState(false);

  const firstSlab = slabs && slabs.length > 0 ? slabs[0] : null;

  const openLightbox = () => {
    if (!firstSlab) return;
    setLightboxOpen(true);
  };

  const closeLightbox = useCallback(() => {
    setLightboxOpen(false);
  }, []);

  // Keyboard handling
  useEffect(() => {
    if (!lightboxOpen) return;
    const handleKeyDown = (e) => {
      if (e.key === "Escape") closeLightbox();
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [lightboxOpen, closeLightbox]);

  // Lock body scroll when lightbox is open
  useEffect(() => {
    if (lightboxOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [lightboxOpen]);

  if (!firstSlab) return null;

  const handleRequestMoreClick = (e) => {
    e.stopPropagation();
    if (typeof onRequestMoreImages === "function") {
      onRequestMoreImages();
    }
  };

  return (
    <>
      {/* Slab Grid - 1 real + 1 blurred placeholder */}
      <div
        data-testid="slab-gallery-grid"
        className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6"
      >
        {/* Real slab tile */}
        <Reveal>
          <div
            role="button"
            tabIndex={0}
            onClick={openLightbox}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                openLightbox();
              }
            }}
            data-testid={`slab-tile-${firstSlab.id}`}
            aria-label={`View slab ${firstSlab.id} in detail`}
            className="group relative cursor-pointer overflow-hidden bg-brand-surface ring-1 ring-white/5 transition-all duration-300 hover:ring-brand-gold/45 focus-visible:ring-brand-gold/60 focus-visible:outline-none aspect-[16/9]"
          >
            <MarbleImage
              src={firstSlab.image}
              alt={firstSlab.alt}
              size="slab"
              wrapperClassName="absolute inset-0"
              className="transition-transform duration-700 ease-out group-hover:scale-105"
            />
            <div className="absolute top-3 left-3">
              <span className="inline-flex items-center justify-center px-2.5 py-1 bg-brand-gold/90 backdrop-blur-sm text-brand-charcoal font-sans text-xs font-medium rounded-full">
                {String(firstSlab.id).padStart(2, "0")}
              </span>
            </div>
            <div className="absolute inset-0 bg-brand-charcoal/0 group-hover:bg-brand-charcoal/20 transition-colors duration-300" />
          </div>
        </Reveal>

        {/* Blurred placeholder tile with "Request More Images" CTA */}
        <Reveal delay={0.05}>
          <div
            data-testid="slab-request-more-tile"
            className="group relative overflow-hidden bg-brand-surface ring-1 ring-white/5 aspect-[16/9]"
          >
            {/* Blurred image background (same as first slab) */}
            <MarbleImage
              src={firstSlab.image}
              alt=""
              aria-hidden="true"
              size="slab"
              wrapperClassName="absolute inset-0"
              className="scale-110"
              style={{ filter: "blur(18px) brightness(0.65) saturate(0.85)" }}
            />
            {/* Dark overlay for legibility */}
            <div className="absolute inset-0 bg-brand-charcoal/55" />

            {/* Center button */}
            <div className="absolute inset-0 flex items-center justify-center px-6">
              <button
                type="button"
                onClick={handleRequestMoreClick}
                data-testid="slab-request-more-btn"
                className="inline-flex items-center gap-2.5 px-5 md:px-7 py-3 md:py-3.5 bg-brand-gold text-brand-charcoal font-sans text-xs md:text-sm font-medium uppercase tracking-[0.2em] transition-all duration-300 hover:bg-brand-gold/90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-gold focus-visible:ring-offset-2 focus-visible:ring-offset-brand-charcoal shadow-lg shadow-black/30"
                style={{ borderRadius: "5px" }}
              >
                <ImagePlus className="w-4 h-4" aria-hidden="true" />
                <span>Request More Images</span>
              </button>
            </div>
          </div>
        </Reveal>
      </div>

      {/* Lightbox - only for the single visible slab */}
      {lightboxOpen && (
        <div
          data-testid="slab-lightbox"
          className="fixed inset-0 z-[9999] bg-black/95 backdrop-blur-sm flex items-center justify-center"
          onClick={closeLightbox}
        >
          <button
            onClick={closeLightbox}
            data-testid="lightbox-close"
            aria-label="Close lightbox"
            className="absolute top-4 right-4 z-10 p-2 text-brand-cream/75 hover:text-brand-cream transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-gold rounded"
          >
            <X className="w-8 h-8" />
          </button>
          <div
            className="relative max-w-6xl max-h-[85vh] mx-auto px-6 md:px-16"
            onClick={(e) => e.stopPropagation()}
          >
            <MarbleImage
              src={firstSlab.image}
              alt={firstSlab.alt}
              fill={false}
              eager
              size="slab"
              wrapperClassName="relative w-full h-full"
              className="w-full h-full object-contain"
              data-testid="lightbox-image"
            />
          </div>
        </div>
      )}
    </>
  );
};

export default SlabGallery;
